import {
  Button,
  Grid,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import CancelIcon from '@material-ui/icons/Cancel';
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { useThemes } from "../../../hooks/useThemes";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import {
  deleteTheme,
  newTheme,
  updateTheme
} from "../../../services/themes.service";
import DialogCustom from "../../Common/DialogCustom";
import { Snackbar } from "../../Common/Snackbar";
import { checkAccess } from "../utils/index";
import Period from "./Period";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw"
  },
  subtitle: {
    ...theme.typography.subtitle1
  },
  cancelIcon: {
    color: theme.palette.error.main
  },
  iconText: {
    ...theme.typography.subtitle1,
    cursor: "pointer"
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50
  },
  buttonOutlined: {
    ...theme.typography.subtitle1,
    border: "2px solid",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50
  },
  spacer: {
    marginBottom: 20
  }
}));

const ThemePage = ({
  t,
  history,
  match,
  actionShowSpinner,
}) => {
  const classes = useStyles();
  const { userAccess } = useAuth();
  const { params: { id } } = match;

  const [dataApiRequestPeriod, setDataApiRequestPeriod] = useState({});
  const [fillEdit, setFillEdit] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });

  const [canDeleteTheme, setCanDeleteTheme] = useState(false);
  const [canCreateTheme, setCanCreateTheme] = useState(false);
  const [canUpdateTheme, setCanUpdateTheme] = useState(false);

  const {
    getTheme,
    theme,
    setDeleteTheme,
    success,
    error,
  } = useThemes({
    spinner: actionShowSpinner,
  });

  useEffect(() => {
    if (userAccess) {
      const hasAccessToDeleteTheme = checkAccess(userAccess, 'deleteTheme');
      const hasAccessToCreateTheme = checkAccess(userAccess, 'createTheme');
      const hasAccessToUpdateTheme = checkAccess(userAccess, 'updateTheme');

      setCanDeleteTheme(hasAccessToDeleteTheme);
      setCanCreateTheme(hasAccessToCreateTheme);
      setCanUpdateTheme(hasAccessToUpdateTheme);

      if (!hasAccessToDeleteTheme && !hasAccessToCreateTheme && !hasAccessToUpdateTheme) {
        history.push('/');
      }
      if (id) getTheme(id);
    }
  }, [userAccess]);

  useEffect(() => {
    if (success.disable === "disable") {
      setMessage({
        open: true,
        content: "Status alterado com sucesso!",
        type: "success",
      });
    }
    if (success.create === "create") {
      setTimeout(function () {
        history.push({
          pathname: "/themes",
          state: { successCreateTheme: true }
        });
      });
    }
    if (success.update === "update") {
      setTimeout(function () {
        history.push({
          pathname: "/themes",
          state: { successEditTheme: true }
        });
      });
    }
  }, [success]);

  useEffect(() => {
    if (error.create) {
      setMessage({
        open: true,
        content: error.create,
        type: "error",
        autoHideDuration: null,
      });
    }
    if (error.update) {
      setMessage({
        open: true,
        content: error.update,
        type: "error",
        autoHideDuration: null,
      });
    }
    if (error.getThemesById) {
      setMessage({
        open: true,
        content: error.getThemesById,
        type: "error",
        autoHideDuration: null,
      });
    }
  }, [error]);

  useEffect(() => {
    if (id && !isEmpty(theme) && !fillEdit) {
      setFillEdit(true);
    }

  }, [theme]);

  const handleChangeDataPeriod = (data) => {
    setDataApiRequestPeriod({ ...dataApiRequestPeriod, ...data });
    setMessage({
      open: false,
      content: "",
      type: "",
      autoHideDuration: 4000,
    });
  };

  const handleSave = async () => {
    actionShowSpinner(true);
    setMessage({
      open: false,
      content: "",
      type: "",
      autoHideDuration: 4000,
    });

    const request = {
      idCommittee: (dataApiRequestPeriod.committee && dataApiRequestPeriod.committee.id) || null,
      themeName: dataApiRequestPeriod.descriptionTheme,
      frequencyId: (dataApiRequestPeriod.frequency && dataApiRequestPeriod.frequency.id) || "",
    };

    if (!id) {
      const response = await newTheme(request);
      actionShowSpinner(false);

      if (response.error.length > 0) {
        setMessage({
          open: true,
          content: response.error,
          type: "error",
          autoHideDuration: 4000,
        });

        return;
      }

      history.push({
        pathname: "/themes",
        state: { successCreateTheme: true }
      });
    } else {
      const updateResponse = await updateTheme(id, request);
      actionShowSpinner(false);
      
      if (updateResponse.error) {
        const errorMessage = updateResponse.error.length > 0 ? updateResponse.error.join(', ') : t("error")
     
        setMessage({
          open: true,
          content: errorMessage,
          type: "error",
          autoHideDuration: 4000,
        });

        return;
      }

      history.push({
        pathname: "/themes",
        state: { successEditTheme: true }
      });
    };
  };

  const handleDelete = async () => {
    const response = await deleteTheme(id);
    setOpen(false);

    if(response.error) {
      setMessage({
        open: true,
        content: response.error.length > 0 ? response.error : t("forms.theme.errorDelete"),
        type: "error",
        autoHideDuration: 4000,
      });

      return;
    }

    history.push({
      pathname: "/themes",
      state: { successDeleteTheme: true }
    });
  };

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Grid container className={classes.root}>
      <Grid container item direction="row" justify="space-between" className={classes.spacer}>
        <Grid item md={6} lg={6}>
          <Typography variant="h4">
            {t(`forms.theme.${id ? "edit" : "new"}`)}
          </Typography>
        </Grid>
        {
          id && (
            <Grid container item direction="row" md={6} lg={6} alignItems="center" justify="center">
              <IconButton disabled={!canDeleteTheme} onClick={handleClickOpen}>
                <CancelIcon className={classes.cancelIcon} />
              </IconButton>
              <Typography
                className={classes.iconText}
                onClick={() => {
                  if (canDeleteTheme) {
                    handleClickOpen();
                  }
                }}
              >
                {t(`forms.dialog.delete`)}
              </Typography>
            </Grid>
          )
        }
        <div>
          <DialogCustom
            open={open}
            handleClose={handleClose}
            handleChange={handleDelete}
            title={t(`forms.dialog.warning`)}
            body={t("forms.dialog.textDelete")}
            confirmText={t(`forms.dialog.yes`)}
            cancelText={t(`forms.dialog.no`)}
            error={true}
          />
        </div>
      </Grid>
      <Grid container item direction="row" className={classes.spacer}>
        <Grid item md={12} lg={12}>
          <Period
            onChangeData={handleChangeDataPeriod}
            spinner={actionShowSpinner}
            theme={theme}
          />
        </Grid>
      </Grid>
      <Grid container item direction="row" justify="center" spacing={2} >
        <Grid item>
          <Button
            variant="outlined"
            className={classes.buttonOutlined}
            onClick={() => history.push("/themes")}
          >
            {t("cancel")}
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            className={classes.button}
            onClick={handleSave}
            disabled={(!id && !canCreateTheme) || (id && !canUpdateTheme)}
          >
            {t("saveTheme")}
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        message={message.content}
        type={message.type}
        open={message.open}
        autoHideDuration={message.autoHideDuration}
      />
    </Grid>
  );
};

const withRouterThemePage = withRouter(ThemePage);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const ThemePageConnect = connect(
  null,
  mapDispatchToProps
)(withRouterThemePage);

export default withTranslation()(ThemePageConnect);
